<script setup lang="ts">
import { computed } from 'vue';
import { TooltipProps } from '@components/tooltip-props.js';
import { DisabledPropsInterface, useDisableComposable } from '@src/application/composables/disabled-composable.js';

const { disabled } = useDisableComposable();

const props = withDefaults(defineProps<DisabledPropsInterface & TooltipProps>(), {
    position: 'bottom_left',
    context: 'default',
    color: 'default',
    changeTextColorOnHover: false,
    delay: 0.4,
    expansion: false,
    alignContent: 'center',
});

const tooltipColor = computed(() => {
    if (props.context !== 'default' && props.color === 'default') {
        return props.context;
    }
    if (props.color !== 'default' && props.context === 'default') {
        return props.color;
    }
    if (props.context !== 'default' && props.color !== 'default') {
        console.error('prop context and prop color can not be set on the same time');
    }
    return 'default';
});
</script>

<template>
    <div
        :class="[
            $style.tooltip__wrapper,
            $style[tooltipColor],
            expansion ? $style.expansion : '',
            disabled && showWhileDisabled !== true ? $style.disabled : $style.show_tooltip,
        ]"
    >
        <div
            :class="[
                $style.content,
                alignContent ? $style[alignContent] : '',
                changeTextColorOnHover ? $style.text_color_on_hover : '',
            ]"
        >
            <slot/>
        </div>
        <p :class="[$style.tooltip, position ? $style[position] : '']">{{ tooltip }}</p>
    </div>
</template>

<style lang="scss" module>
@use '@text' as *;
@use '@backgrounds' as *;

.tooltip__wrapper {
    position: relative;

    &.expansion {
        width: 100%;
        height: 100%;
    }
    .content {
        display: flex;
        height: 100%;

        &.center {
            align-items: center;
            justify-content: center;
        }
        &.grow {
            width: 100%;

            > * {
                flex-grow: 1;
            }
        }
    }
    .tooltip {
        position: absolute;
        z-index: 89;
        visibility: hidden;
        transition: visibility;
    }

    &.show_tooltip:hover {
        cursor: pointer;
        * { cursor: inherit; }

        .tooltip {
            padding: .3em .5em;
            font-size: 90%;
            font-weight: $weight-medium;
            line-height: 1.1;
            color: #fff;
            white-space: nowrap;
            visibility: visible;
            border-radius: 3px;
            transition-delay: v-bind('delay + "s"');

            &::before {
                position: absolute;
                width: 15px;
                height: 10px;
                clip-path: polygon(0 100%, 50% 20%, 100% 100%);
                content: '';
                background-color: inherit;
            }
        }
        .text_color_on_hover * {
            color: inherit;
        }
        &.default {
            .text_color_on_hover {
                color: #6a6a6a;
            }
            .tooltip { background-color: #9d9d9d; }
        }
        &.primary {
            .text_color_on_hover {
                color: $primary-text-color;
            }
            .tooltip { background-color: $primary-background; }
        }
        &.danger, &.red {
            .text_color_on_hover {
                color: $red-text-color;
            }
            .tooltip { background-color: $red-background; }
        }
        &.warning, &.yellow {
            .text_color_on_hover {
                color: $yellow-text-color;
            }
            .tooltip {
                color: $default-text-color;
                background-color: $yellow-background;
            }
        }
        &.orange {
            .text_color_on_hover {
                color: $orange-text-color;
            }
            .tooltip { background-color: $orange-background; }
        }
        &.green {
            .text_color_on_hover {
                color: $green-text-color;
            }
            .tooltip { background-color: $green-background; }
        }
        &.blue {
            .text_color_on_hover {
                color: $blue-text-color;
            }
            .tooltip { background-color: $blue-background; }

        }
        &.grey {
            .tooltip { background-color: #aaa5a5 }
        }
    }
    &.disabled:hover {
        cursor: not-allowed;
    }
}

.tooltip {
    &.bottom_left {
        top: calc(100% + 10px);
        left: calc(50% - 14px);

        &::before {
            top: -9px;
            left: 6px;
        }
    }
    &.bottom_right {
        top: calc(100% + 10px);
        right: calc(50% - 14px);

        &::before {
            top: -9px;
            right: 6px;
        }
    }
    &.left {
        top: calc(50% - 10px);
        right: calc(100% + 10px);
        text-align: right;

        &::before {
            top: 6px;
            right: -12px;
            transform: rotate(90deg);
        }
    }
    &.right {
        top: calc(50% - 10px);
        left: calc(100% + 10px);

        &::before {
            top: 6px;
            left: -12px;
            transform: rotate(270deg);
        }
    }
    &.top{
        bottom: calc(100% + 10px);
        left: calc(50% - 14px);

        &::before {
            top: 21px;
            left: 6px;
            transform: rotate(180deg);
        }
    }
}
</style>
